import React, { useEffect, useState, useRef } from "react";
import { transactionAPI } from 'services';
import { formatMoneyInput } from "utils";
import DatePicker from "react-datepicker";
import { toast } from "sonner";
import useUniversalStore from "store/universalStore";
import useOffcanvasStore from "store/offcanvasStore";
import { useForm, Controller } from "react-hook-form";
import { defaultValues, validateAmount } from "./config";
import FilePicker from "views/FilePicker";

const amountRules = {
  required: "Amount is required",
  validate: validateAmount
};

const EditTxn = () => {
  const { transactionId, accountId, write } = useUniversalStore(state => state);
  const { closeOffcanvas } = useOffcanvasStore();
  const { control, handleSubmit, setValue, register, watch, formState: { errors, isSubmitting, isValid, dirtyFields } } = useForm({
    defaultValues,
    mode: 'onChange'
  });
  const [loading, setLoading] = useState(false);
  const [isKHATA, setIsKHATA] = useState(false);

  const filePickerRef = useRef(null);
  const attachment = watch("attachment");
  const isCash = watch("isCash"); // Watch isCash to trigger re-renders on toggle

  useEffect(() => {
    if (location.pathname === "/") {
      setIsKHATA(false);
    } else if (location.pathname === "/khatabook") {
      setIsKHATA(true);
    }
  }, []);


  useEffect(() => {
    fetchTransactionDetails();
  }, []);

  const fetchTransactionDetails = async () => {
    setLoading(true);
    try {
      const transaction = await transactionAPI.getTransactionById(transactionId);
      if (transaction) {
        setValue("amount", transaction.get("amount") ?? 0);
        setValue("date", transaction.get("date") ? new Date(transaction.get("date")) : new Date());
        setValue("detail", transaction.get("detail") || "");
        setValue("isCash", transaction.get("type") == "CASH" ? true : false);
        setValue("attachment", { url: transaction.get("attachment") || "", file: null, loading: false });
      }
    } catch (error) {
      toast.error("Failed to fetch transaction details");
    } finally {
      setLoading(false);
    }
  };

  const onAmountChange = (e, onChange) => {
    const rawValue = e.target.value.replace(/[^0-9.-]/g, "").match(/^-?\d*\.?\d{0,2}/);
    const match = rawValue ? rawValue[0] : "";
    const parts = match.split('.');
    let cleanedValue = parts[0];
    if (parts.length > 1) {
      cleanedValue += '.' + parts[1].substring(0, 2);
    }

    if (cleanedValue === "") {
      onChange(0);
      return;
    }

    onChange(cleanedValue);
  };

  const onSubmit = async (data) => {
    try {
      const amountNumber = Number(data.amount);

      if (Number.isNaN(amountNumber)) {
        throw new Error("Invalid amount: must be a number");
      }
      data.date.setHours(data.date.getHours(), data.date.getMinutes(), data.date.getSeconds());
      await transactionAPI.updateTransaction(transactionId, amountNumber, data.detail, data.date, data.attachment?.url, data.isCash);
      write({
        transactions: await transactionAPI.getTransactionsByAccountIdWithMonth(accountId, data.date),
        transactionId: null,
        selectedMonth: data.date
      });
      closeOffcanvas();
      toast.success("Transaction updated successfully");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const inputClass = (name) => `form-control ${errors[name] ? 'is-invalid' : ''}`;
  const labelStyle = { fontSize: "0.75rem" };

  const buttonDisabled =
    attachment?.loading ||
    isSubmitting ||
    errors.amount ||
    loading;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>

      <div className="d-flex mb-3">
        <div className="w-50">
          <Controller
            name="amount"
            control={control}
            rules={amountRules}
            render={({ field: { value, onChange, ...rest } }) => (
              <div className="form-control border-0 p-0">
                <label className="text-app" style={labelStyle}>Amount</label>
                <input
                  {...rest}
                  id="editTxnAmountInput"
                  type="text"
                  placeholder="Amount"
                  className={inputClass("amount")}
                  value={formatMoneyInput(value)}
                  disabled={isSubmitting || loading}
                  onChange={(e) => onAmountChange(e, onChange)}
                />
              </div>
            )}
          />
          {errors.amount && <div className="invalid-feedback d-block">{errors.amount.message}</div>}
        </div>

        <div className="ms-3">
          <Controller
            name="date"
            control={control}
            rules={{ required: "Date is required" }}
            render={({ field: { value, onChange } }) => (
              <div className="form-control border-0 p-0">
                <label className="text-app" style={labelStyle}>Date</label>
                <DatePicker
                  selected={value}
                  onChange={onChange}
                  dateFormat="dd-MM-yyyy"
                  maxDate={new Date()}
                  className="form-control"
                  popperPlacement="bottom-start"
                  wrapperClassName="w-100"
                  disabled={isSubmitting || loading}
                />
              </div>
            )}
          />
          {errors.date && <div className="invalid-feedback d-block">{errors.date.message}</div>}
        </div>
      </div>

      <div className="mb-3">
        <div className="border-0 p-0">
          <label className="text-app" style={labelStyle}>Detail</label>
          <div className="form-control p-0 d-flex align-items-end ">
            <textarea
              {...register("detail")}
              placeholder="Details"
              className="form-control border-0 p-0 shadow-none"
              rows={2}
              maxLength={50}
              disabled={isSubmitting || loading}
            />
            <button
              type="button"
              className="btn text-app border-0 mb-1"
              onClick={() => filePickerRef.current?.click()}
              title="Attach File"
              disabled={isSubmitting || loading}
            >
              <i className="bi bi-paperclip fs-4"></i>
            </button>
          </div>
        </div>
        {errors.detail && <div className="invalid-feedback d-block">{errors.detail.message}</div>}
      </div>
      <FilePicker
        ref={filePickerRef}
        onPick={(field, value) => setValue(field, value, { shouldValidate: true })}
        loading={attachment?.loading}
        initialUrl={attachment?.url}
      />

      <div className="px-3 position-absolute bottom-0 start-0 end-0 mb-3">
        {isKHATA && <div className="d-flex mb-3 justify-content-between">
          {/* Option to post only in Ledger and not Cashbook should be shown only in Khata */}
          <Controller
            name="isCash"
            control={control}
            render={({ field: { value } }) => (
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  role="button"
                  type="checkbox"
                  checked={value}
                  onChange={(e) => setValue("isCash", !value, { shouldDirty: true })}
                  id="isCashCheckbox"
                />
                <label className="form-check-label" htmlFor="isCashCheckbox">
                  Add to Cashbook
                </label>
              </div>
            )}
          />
        </div>}
        <button
          type="submit"
          className="btn btn-app w-100 py-3 fs-5 fw-bold text-uppercase"
          // The button remains enabled (if no other blockers aside from isValid) when isCash toggled
          disabled={buttonDisabled}
        >
          <span className="me-2">Update</span>
          {isSubmitting ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : null}
        </button>
      </div>
    </form>
  )
}
export default EditTxn;
